import type { FC } from 'react';
import React, { useContext } from 'react';
import { Trans } from 'react-i18next';
import { Box, List, ListItem, Typography } from '@mui/material';
import _ from 'lodash';

import type { BaseUser, Role } from 'src/types/user';
import type { ModuleRole } from 'src/types/userModule';
import { BulkEditContext } from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialog';

import { styles } from './bulkEditPermissionSummary.styles';

interface usersPrevRolesProps {
  role: Role;
  users: BaseUser[];
}

export interface BulkEditPermissionSummaryProps {
  changedAttributes: string[][];
  moduleRoles: ModuleRole[];
}

const moduleRoleReducer = (
  moduleRoles: ModuleRole[],
  roleId: number | string,
) =>
  moduleRoles.reduce(
    (acc: number[], moduleRole) =>
      moduleRole.role.id === Number(roleId)
        ? [...acc, moduleRole.module.id]
        : acc,
    [],
  );

const compareRolesModulePermissions = (
  roleId: number | string,
  rolesToCompare: Role[],
  moduleRoles: ModuleRole[],
) => {
  if (!Array.isArray(moduleRoles)) return [];
  const newRoleModules = moduleRoleReducer(moduleRoles, roleId);
  return rolesToCompare.reduce((acc: Role[], role) => {
    const d = _.difference(
      moduleRoleReducer(moduleRoles, role.id),
      newRoleModules,
    ).length;
    return d ? [...acc, role] : acc;
  }, []);
};

/**
 * Renders a summary of the changing user permissions for the changed attributes.
 */
const BulkEditPermissionSummary: FC<BulkEditPermissionSummaryProps> = ({
  changedAttributes,
  moduleRoles,
}) => {
  const { formFields, selectedUsers } = useContext(BulkEditContext);

  const [fieldName, newRole] =
    changedAttributes.find((attribute) => attribute.includes('role')) || [];
  const field = formFields.find((attribute) => attribute.name === fieldName);
  const choice = field?.choices.find((choice) => choice.value === newRole);

  if (!(selectedUsers?.length && choice)) return null;

  const previousRoles = selectedUsers.reduce(
    (acc: Role[], user) =>
      user.role && !acc.find(({ id }) => id === user.role?.id)
        ? [...acc, user.role]
        : acc,
    [],
  );

  const roleDifferences = compareRolesModulePermissions(
    newRole,
    previousRoles,
    moduleRoles,
  );

  // Only consider users whose role actually changes
  const usersPrevRoles: usersPrevRolesProps[] = roleDifferences.reduce(
    (acc: usersPrevRolesProps[], role) =>
      role.toString() !== newRole.toString()
        ? [
            ...acc,
            {
              role: role,
              users: selectedUsers.filter(
                (user: BaseUser) => user.role?.id === role.id,
              ),
            },
          ]
        : acc,
    [],
  );

  const getPermissionMessage = (role: Role) => ({
    oldValue: role.name || '-',
    newValue: choice?.label || '-',
  });

  return usersPrevRoles.length ? (
    <>
      <Typography variant="h4">
        <Trans i18nKey="admin:BulkEdit.ConfirmationHeaderPermissions" />
      </Typography>
      {usersPrevRoles.map(
        (usersPerPreviousRole, i) =>
          usersPerPreviousRole.users && (
            <Box key={`usersPrevRoles-${i}`} sx={styles.permissionMessageBox}>
              <Typography color="gray" sx={styles.permissionMessage}>
                <Trans
                  i18nKey="admin:BulkEdit.ConfirmationChangePermissionMessage"
                  values={getPermissionMessage(usersPerPreviousRole.role)}
                />
              </Typography>
              <List sx={styles.roleChangeList}>
                {usersPerPreviousRole.users.map((user, i) => (
                  <ListItem dense key={`user-list-${i}`} sx={styles.listItem}>
                    {user.first_name} {user.last_name}
                  </ListItem>
                ))}
              </List>
            </Box>
          ),
      )}
    </>
  ) : null;
};

export default BulkEditPermissionSummary;
